import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Modal, Switch, Button, Checkbox, Typography, Tabs, Empty, Spin, Badge, message } from 'antd';
import { UpCircleFilled, DownCircleFilled } from '@ant-design/icons';
import {
  closeTabsExport,
  setToggle,
  selectAllForTab,
  toggleItem,
  setActiveTab,
  submitExportRequest,
} from './export-actions';
import { getActiveTab, getTabsExportState, getToggleValue, getIsSubmitting } from './export-selectors';

const { Text } = Typography;
const { TabPane } = Tabs;

/* ---------- Styles ---------- */
const Container = styled.div`
  height: 400px;
  overflow: hidden;
`;

const StyledTabs = styled(Tabs).attrs({
  moreIcon: (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px', // space between arrows
        fontSize: '14px', // arrow size
        width: '100%',
      }}
    >
      <UpCircleFilled />
      <DownCircleFilled />
    </div>
  ),
})`
  height: 100%;
  display: flex;
  flex-direction: row;

  /* Left navigation tabs */
  .ant-tabs-nav {
    flex: none;
    width: 200px;
    border-right: 1px solid #f0f0f0;
    overflow-y: auto;
  }

  .ant-tabs-nav::-webkit-scrollbar {
    width: 10px;
  }
  .ant-tabs-nav::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 6px;
  }

  /* Content area */
  .ant-tabs-content-holder {
    flex: 1;
    overflow: hidden;
  }

  .ant-tabs-content {
    height: 100%;
  }

  .ant-tabs-tabpane {
    height: 100%;
    overflow: hidden; /* ✅ only active tab is shown */
  }
  .ant-tabs-tab {
    font-size: 12px; /* ✅ reduced font size */
    padding: 6px 12px; /* tighter padding */
    font-weight: 500;
  }

  .ant-tabs-tab-active {
    font-weight: 600;
  }
`;

const RightPane = styled.div`
  height: 100%;
  padding: 16px;
  min-width: 430px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 6px;
  }

  .ant-checkbox-wrapper {
    background: none !important;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 0;
`;

const FooterLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ExportModalWrapper = styled(Modal)`
  &.export-modal {
    top: 40px; /* ✅ moves modal upward */
  }

  &.export-modal > .ant-modal-content {
    width: auto !important;
    max-width: 700px;
    margin: 0 auto; /* ✅ horizontally centered */
  }
`;

/* ---------- Component ---------- */
export function TabsExportDialog() {
  const dispatch = useDispatch();

  const { open, loading, data, selected } = useSelector(getTabsExportState);
  const activeTab = useSelector(getActiveTab);
  const toggleValue = useSelector(getToggleValue);
  const isSubmitting = useSelector(getIsSubmitting);

  const renderRightPane = (tabKey: string) => {
    const list = data[tabKey] || [];
    const selectedSet = selected[tabKey] || new Set();

    if (!list.length)
      return (
        <RightPane>
          <Empty description='No fields' />
        </RightPane>
      );

    const allChecked = list.length > 0 && selectedSet.size === list.length;
    const indeterminate = selectedSet.size > 0 && selectedSet.size < list.length;

    return (
      <RightPane>
        <Row>
          <Checkbox
            checked={allChecked}
            indeterminate={indeterminate}
            onChange={(e) => dispatch(selectAllForTab(tabKey, e.target.checked))}
          >
            <span style={{ fontWeight: 'bold' }}> Select All</span>
          </Checkbox>
        </Row>

        {list.map((label) => (
          <CheckboxRow key={label}>
            <Checkbox
              checked={selectedSet.has(label)}
              onChange={(e) => dispatch(toggleItem(tabKey, label, e.target.checked))}
            >
              {label}
            </Checkbox>
          </CheckboxRow>
        ))}
      </RightPane>
    );
  };

  return (
    <ExportModalWrapper
      className='export-modal'
      title='Data Export Options'
      visible={open}
      onCancel={() => dispatch(closeTabsExport())}
      footer={null}
      width={1100}
      bodyStyle={{ padding: 16 }}
      destroyOnClose
    >
      {loading ? (
        <Spin />
      ) : (
        <Container>
          <StyledTabs tabPosition='left' activeKey={activeTab} onChange={(key) => dispatch(setActiveTab(key))} animated>
            {Object.keys(data).map((tabKey) => {
              const hasSelection = (selected[tabKey] || new Set()).size > 0;
              const displayName: string = tabKey.replace(/_/g, ' ');
              return (
                <TabPane
                  tab={
                    hasSelection ? (
                      <Badge dot color='green'>
                        <span style={{ fontWeight: 600, fontSize: 12 }}>{displayName}</span>
                      </Badge>
                    ) : (
                      <span style={{ fontSize: 12 }}>{displayName}</span>
                    )
                  }
                  key={tabKey}
                >
                  {renderRightPane(tabKey)}
                </TabPane>
              );
            })}
          </StyledTabs>
        </Container>
      )}

      {/* Footer */}
      <Row style={{ marginTop: 16, justifyContent: 'space-between' }}>
        <FooterLeft>
          <ToggleRow>
            <span>Blank Cells</span>
            <Switch
              checked={toggleValue === 'Existing'}
              onChange={(val) => dispatch(setToggle(val ? 'Existing' : 'Blank'))}
            />
            <span>Existing Data</span>
          </ToggleRow>
        </FooterLeft>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button onClick={() => dispatch(closeTabsExport())}>Cancel</Button>
          <Button
            type='primary'
            onClick={() => {
              const hasAnySelection = Object.values(selected).some((set: Set<string>) => set && set.size > 0);
              if (!hasAnySelection) {
                message.error('Please select at least one column from any tab to export');
                return;
              }
              dispatch(submitExportRequest());
            }}
            loading={isSubmitting}
            disabled={!Object.keys(data).length}
          >
            Download
          </Button>
        </div>
      </Row>
    </ExportModalWrapper>
  );
}
